import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  alpha,
  useTheme,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Google as GoogleIcon
} from '@mui/icons-material';
import { supabase } from '../supabaseClient';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../theme';
import InvitationPortal from './InvitationPortal';

const AuthGate = ({ onBackToLanding }) => {
  const muiTheme = useTheme();
  const ENABLE_GOOGLE_AUTH = false; // Set to true when Google OAuth is ready
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [showInvitationPortal, setShowInvitationPortal] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // Password validation helper
  const validatePassword = (pwd) => {
    if (!pwd || pwd.length < 10) return false;
    
    const hasLowercase = /[a-z]/.test(pwd);
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasDigit = /\d/.test(pwd);
    const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd);
    
    return hasLowercase && hasUppercase && hasDigit && hasSymbol;
  };

  // Compute form validity without side effects
  const isFormValid = React.useMemo(() => {
    if (isPasswordReset) {
      return newPassword && 
             validatePassword(newPassword) && 
             confirmNewPassword && 
             newPassword === confirmNewPassword;
    } else if (isSignUp) {
      return email.trim() && 
             password && 
             validatePassword(password) && 
             confirmPassword && 
             password === confirmPassword;
    } else {
      return email.trim() && password;
    }
  }, [email, password, confirmPassword, isSignUp, isPasswordReset, newPassword, confirmNewPassword]);

  // Update validation message when inputs change
  React.useEffect(() => {
    if (isPasswordReset) {
      if (!newPassword && !confirmNewPassword) {
        setValidationMessage('');
        return;
      }

      if (newPassword && newPassword.length < 10) {
        setValidationMessage('Password must be at least 10 characters long');
      } else if (newPassword && !validatePassword(newPassword)) {
        // Check specific requirements and show the first missing one
        if (!/[a-z]/.test(newPassword)) {
          setValidationMessage('Password must contain at least 1 lowercase letter');
        } else if (!/[A-Z]/.test(newPassword)) {
          setValidationMessage('Password must contain at least 1 uppercase letter');
        } else if (!/\d/.test(newPassword)) {
          setValidationMessage('Password must contain at least 1 number');
        } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)) {
          setValidationMessage('Password must contain at least 1 symbol:\n• Special characters: ! @ # $ % ^ & *\n• Punctuation: ( ) _ + - = [ ] { } | ; \' " \\ , . / < > ?');
        }
      } else if (newPassword && confirmNewPassword && newPassword !== confirmNewPassword) {
        setValidationMessage('Passwords do not match');
      } else {
        setValidationMessage('');
      }
    } else if (!email && !password && !confirmPassword) {
      setValidationMessage('');
      return;
    } else if (isSignUp) {
      if (password && password.length < 10) {
        setValidationMessage('Password must be at least 10 characters long');
      } else if (password && !validatePassword(password)) {
        // Check specific requirements and show the first missing one
        if (!/[a-z]/.test(password)) {
          setValidationMessage('Password must contain at least 1 lowercase letter');
        } else if (!/[A-Z]/.test(password)) {
          setValidationMessage('Password must contain at least 1 uppercase letter');
        } else if (!/\d/.test(password)) {
          setValidationMessage('Password must contain at least 1 number');
        } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
          setValidationMessage('Password must contain at least 1 symbol:\n• Special characters: ! @ # $ % ^ & *\n• Punctuation: ( ) _ + - = [ ] { } | ; \' " \\ , . / < > ?');
        }
      } else if (password && confirmPassword && password !== confirmPassword) {
        setValidationMessage('Passwords do not match');
      } else {
        setValidationMessage('');
      }
    } else {
      // No validation messages for sign in
      setValidationMessage('');
    }
  }, [email, password, confirmPassword, isSignUp, isPasswordReset, newPassword, confirmNewPassword]);
  const [invitationToken, setInvitationToken] = useState('');
  const [invitedEmail, setInvitedEmail] = useState('');
  const [invitedCompany, setInvitedCompany] = useState('');
  const [invitedRole, setInvitedRole] = useState('');
  const [invitationValidationState, setInvitationValidationState] = useState('none'); // 'none', 'checking', 'valid', 'invalid'
  const [invitationError, setInvitationError] = useState('');

  const validateInvitationToken = async (token, email) => {
    try {
      const { data, error } = await supabase
        .rpc('validate_invitation_token', { 
          p_invitation_token: token, 
          p_email: email 
        });

      if (error) {
        console.error('Error validating invitation:', error);
        setInvitationValidationState('invalid');
        setInvitationError('Unable to validate invitation. Please try again later.');
        return false;
      }

      const result = data[0];
      if (!result.is_valid) {
        setInvitationValidationState('invalid');
        setInvitationError(result.error_message);
        return false;
      }

      setInvitationValidationState('valid');
      return true;
    } catch (err) {
      console.error('Invitation validation error:', err);
      setInvitationValidationState('invalid');
      setInvitationError('Unable to validate invitation. Please try again later.');
      return false;
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const inviteEmail = urlParams.get('email');
    const company = urlParams.get('company');
    const role = urlParams.get('role');
    const mode = urlParams.get('mode');
    const confirmed = urlParams.get('confirmed');
    

    
    if (token && inviteEmail && mode === 'invite') {
      setInvitationToken(token);
      setInvitedEmail(inviteEmail);
      setInvitedCompany(company || '');
      setInvitedRole(role || '');
      
      // Validate invitation before showing portal
      setInvitationValidationState('checking');
      validateInvitationToken(token, inviteEmail).then(isValid => {
        if (isValid) {
          setShowInvitationPortal(true);
          localStorage.setItem('pendingInvitationToken', token);
          localStorage.setItem('pendingInvitationEmail', inviteEmail);
          localStorage.setItem('pendingInvitationCompany', company || '');
          localStorage.setItem('pendingInvitationRole', role || '');
        }
      });
      
      const cleanUrl = window.location.origin + '/auth';
      if (window.location.pathname !== '/auth') {
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } else if (confirmed === 'true' && inviteEmail) {
      const hasInvitation = urlParams.get('has_invitation');
      const pendingCompany = localStorage.getItem('pendingInvitationCompany');
      
      if (hasInvitation === 'true' && pendingCompany) {
        setSuccess(`Email confirmed! Sign in below to join ${pendingCompany}.`);
      } else {
        setSuccess('Email confirmed! Please sign in to your account.');
      }
      setEmail(inviteEmail);
      
      const cleanUrl = window.location.origin + '/auth';
      if (window.location.pathname !== '/auth') {
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } else if (mode === 'reset') {
      setIsPasswordReset(true);
      setEmail(urlParams.get('email') || '');
      setSuccess('Please enter your new password below.');
      
      // Store the token in localStorage so we don't lose it when cleaning URL
      const resetToken = urlParams.get('token');
      if (resetToken) {
        localStorage.setItem('passwordResetToken', resetToken);
      }
      
      const cleanUrl = window.location.origin + '/auth';
      if (window.location.pathname !== '/auth') {
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } else if (mode === 'confirm') {
      // Handle email confirmation from the new flow
      const confirmToken = urlParams.get('token');
      if (confirmToken) {
        // Call the confirm-signup function with proper authorization
        setLoading(true);
        fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/confirm-signup?token=${confirmToken}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY
          }
        })
          .then(response => {
            if (response.redirected) {
              // Function redirected us, follow the redirect
              window.location.href = response.url;
            } else if (response.ok || response.status === 302) {
              return response.text().then(() => {
                // Show success message but DON'T try to accept invitation here
                // The invitation will be accepted when they sign in
                const pendingToken = localStorage.getItem('pendingInvitationToken');
                if (pendingToken) {
                  setSuccess('Email confirmed! Please sign in with your password to continue.');
                } else {
                  setSuccess('Email confirmed! Please sign in to your account.');
                }
                setLoading(false);
              });
            } else {
              // Request failed
              throw new Error(`Confirmation failed with status ${response.status}`);
            }
          })
          .catch(error => {
            console.error('Confirmation error:', error);
            setError('Failed to confirm account. Please try again.');
            setLoading(false);
          });
      }
      
      const cleanUrl = window.location.origin + '/auth';
      if (window.location.pathname !== '/auth') {
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } else {
      // Check for pending invitation on page load (if user is already signed in)
      const pendingToken = localStorage.getItem('pendingInvitationToken');
      const pendingEmail = localStorage.getItem('pendingInvitationEmail');
            
      if (pendingToken && pendingEmail) {
        supabase.auth.getUser().then(({ data: { user } }) => {
          if (user && user.email?.toLowerCase() === pendingEmail.toLowerCase()) {
            supabase.rpc('accept_invitation', { p_invitation_token: pendingToken })
              .then(({ error }) => {
                if (!error) {
                  // SECURITY FIX: Clear ALL cached company data before redirect
                  localStorage.removeItem('pendingInvitationToken');
                  localStorage.removeItem('pendingInvitationEmail');
                  localStorage.removeItem('pendingInvitationCompany');
                  localStorage.removeItem('pendingInvitationRole');
                  localStorage.removeItem('currentCompanyId');
                  localStorage.removeItem('userPermissions');
                  
                  // Force a complete reload to ensure fresh company context
                  window.location.href = '/?invitation_accepted=true';
                } else {
                  console.error('Failed to accept invitation:', error);
                  setError('Failed to accept invitation: ' + error.message);
                }
              });
          }
        });
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setValidationMessage('');
    setLoading(true);

    try {
      if (isPasswordReset) {
        // Validate password requirements
        if (!validatePassword(newPassword)) {
          if (newPassword.length < 10) {
            throw new Error('Password must be at least 10 characters long');
          } else if (!/[a-z]/.test(newPassword)) {
            throw new Error('Password must contain at least 1 lowercase letter');
          } else if (!/[A-Z]/.test(newPassword)) {
            throw new Error('Password must contain at least 1 uppercase letter');
          } else if (!/\d/.test(newPassword)) {
            throw new Error('Password must contain at least 1 number');
          } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)) {
            throw new Error('Password must contain at least 1 symbol:\n• Special characters: ! @ # $ % ^ & *\n• Punctuation: ( ) _ + - = [ ] { } | ; \' " \\ , . / < > ?');
          }
        }

        // Validate password confirmation
        if (newPassword !== confirmNewPassword) {
          throw new Error('Passwords do not match');
        }

        // Get reset token from localStorage
        const resetToken = localStorage.getItem('passwordResetToken');
        
        if (!resetToken) {
          throw new Error('Invalid reset session. Please request a new password reset.');
        }

        const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/update-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            token: resetToken,
            password: newPassword
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to update password');
        }

        setSuccess('Password updated successfully! Redirecting to sign in...');
        setNewPassword('');
        setConfirmNewPassword('');
        
        // Clear the reset token
        localStorage.removeItem('passwordResetToken');
        
        // Wait a moment then redirect to sign in
        setTimeout(() => {
          window.location.href = '/auth';
        }, 2000);
      } else if (isSignUp) {
        // Validate password requirements
        if (!validatePassword(password)) {
          if (password.length < 10) {
            throw new Error('Password must be at least 10 characters long');
          } else if (!/[a-z]/.test(password)) {
            throw new Error('Password must contain at least 1 lowercase letter');
          } else if (!/[A-Z]/.test(password)) {
            throw new Error('Password must contain at least 1 uppercase letter');
          } else if (!/\d/.test(password)) {
            throw new Error('Password must contain at least 1 number');
          } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
            throw new Error('Password must contain at least 1 symbol:\n• Special characters: ! @ # $ % ^ & *\n• Punctuation: ( ) _ + - = [ ] { } | ; \' " \\ , . / < > ?');
          }
        }

        // Validate password confirmation
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }

        const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/confirm-signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            email: email,
            password: password,
            user_name: email.split('@')[0]
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to create account');
        }

        setSuccess('Account created! Check your email to verify your account.');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        
        const pendingToken = localStorage.getItem('pendingInvitationToken');
        const pendingEmail = localStorage.getItem('pendingInvitationEmail');
        
        if (pendingToken) {
          try {
            const { error: inviteError } = await supabase
              .rpc('accept_invitation', { p_invitation_token: pendingToken });
            
            if (!inviteError) {
              // SECURITY FIX: Clear ALL cached company data
              localStorage.removeItem('pendingInvitationToken');
              localStorage.removeItem('pendingInvitationEmail');
              localStorage.removeItem('pendingInvitationCompany');
              localStorage.removeItem('pendingInvitationRole');
              localStorage.removeItem('currentCompanyId');
              localStorage.removeItem('userPermissions');
              
              // Navigate to home without reload
              window.location.href = '/';
              return; // Don't continue with normal sign-in flow
            } else {
              console.error('Failed to accept invitation:', inviteError);
            }
          } catch (inviteErr) {
            console.error('Exception while accepting invitation:', inviteErr);
          }
        }
        
        // Successful login - the auth state change will handle showing the main app
        // Clear the auth URL
        window.history.replaceState({}, document.title, '/');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!email) {
      setError('Please enter your email first, then click "Forgot your password?"');
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          email: email.toLowerCase().trim()
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send password reset email');
      }

      setSuccess('Password reset link sent! Check your inbox.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await supabase.auth.signInWithOAuth({ provider: 'google' });
    } catch (err) {
      setError(err.message);
    }
  };

  if (showInvitationPortal) {
    return (
      <InvitationPortal
        invitationToken={invitationToken}
        invitedEmail={invitedEmail}
        invitedCompany={invitedCompany}
        invitedRole={invitedRole}
        onSuccess={() => {
          window.location.href = '/';
        }}
      />
    );
  }

  // Show loading state while validating invitation
  if (invitationValidationState === 'checking' && invitationToken) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f7f7f8'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={40} sx={{ color: '#000', mb: 2 }} />
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Validating invitation...
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  // Show expired invitation message
  if (invitationValidationState === 'invalid' && invitationToken) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            background: '#f7f7f8',
            position: 'relative'
          }}
        >
          <Box sx={{ position: 'absolute', top: 32, left: 32, zIndex: 10 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 500,
                color: '#000',
                fontSize: '1.8rem',
                letterSpacing: '0.5px',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                cursor: 'default',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'scale(1.02)',
                  textShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
                }
              }}
            >
              GlowWorks
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 3
            }}
          >
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                maxWidth: 420,
                p: 5,
                borderRadius: 4,
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                backgroundColor: '#fff'
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 500,
                  color: '#000',
                  fontSize: '2rem',
                  mb: 4,
                  letterSpacing: '-0.5px',
                  textAlign: 'center'
                }}
              >
                Invitation Expired
              </Typography>

              <Box
                sx={{
                  mb: 3,
                  p: 3,
                  borderRadius: 1.5,
                  backgroundColor: alpha('#dc2626', 0.1),
                  border: `1px solid ${alpha('#dc2626', 0.2)}`,
                  textAlign: 'center'
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: '#dc2626',
                    fontWeight: 500,
                    lineHeight: 1.5,
                    fontSize: '1rem',
                    mb: 1
                  }}
                >
                  {invitationError}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha('#dc2626', 0.8),
                    fontWeight: 400,
                    lineHeight: 1.5,
                    fontSize: '0.9rem'
                  }}
                >
                  Please contact your administrator for a new invitation.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                <Box
                  component="button"
                  onClick={() => {
                    // Clear invitation data and redirect to auth page without invitation
                    localStorage.removeItem('pendingInvitationToken');
                    localStorage.removeItem('pendingInvitationEmail');
                    localStorage.removeItem('pendingInvitationCompany');
                    localStorage.removeItem('pendingInvitationRole');
                    window.location.href = '/auth';
                  }}
                  sx={{
                    width: '100%',
                    py: 2.5,
                    px: 4,
                    backgroundColor: '#000',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    fontSize: '1rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontFamily: 'inherit',
                    '&:hover': {
                      backgroundColor: '#333',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 16px ${alpha('#000', 0.3)}`
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    }
                  }}
                >
                  Continue to Sign In
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#f7f7f8',
          position: 'relative'
        }}
      >
        <Box sx={{ position: 'absolute', top: 32, left: 32, zIndex: 10 }}>
          <Typography
            variant="h6"
            onClick={() => window.location.href = '/'}
            sx={{
              fontWeight: 500,
              color: '#000',
              fontSize: '1.5rem',
              letterSpacing: '1px',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                color: '#1a202c',
                transform: 'scale(1.05) translateY(-1px)',
                textShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                letterSpacing: '1.5px'
              }
            }}
          >
            GlowWorks
          </Typography>
        </Box>

        <Box sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2
        }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: '400px',
              background: 'transparent'
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 500,
                color: '#000',
                fontSize: '2rem',
                mb: 4,
                letterSpacing: '-0.5px',
                textAlign: 'center'
              }}
            >
              {isPasswordReset ? 'Reset your password' : isSignUp ? 'Create your account' : 'Welcome back'}
            </Typography>

            {error && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: alpha('#ef4444', 0.1),
                  border: `1px solid ${alpha('#ef4444', 0.2)}`
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: '#ef4444', fontWeight: 400, lineHeight: 1.5, fontSize: '0.9rem' }}
                >
                  {error}
                </Typography>
              </Box>
            )}

            {success && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: alpha('#10a37f', 0.1),
                  border: `1px solid ${alpha('#10a37f', 0.2)}`
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: '#10a37f', fontWeight: 400, lineHeight: 1.5, fontSize: '0.9rem' }}
                >
                  {success}
                </Typography>
              </Box>
            )}

            {validationMessage && (
              <Box
                sx={{
                  mb: 3,
                  p: 3,
                  borderRadius: 4,
                  backgroundColor: alpha('#ef4444', 0.1),
                  border: `1px solid ${alpha('#ef4444', 0.2)}`
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ 
                    color: '#ef4444', 
                    fontWeight: 400, 
                    lineHeight: 1.5, 
                    fontSize: '0.9rem',
                    whiteSpace: 'pre-line'
                  }}
                >
                  {validationMessage}
                </Typography>
              </Box>
            )}

            <form onSubmit={handleSubmit}>
              {!isPasswordReset && (
              <TextField
                label="Email address"
                type="email"
                fullWidth
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 4,
                    fontSize: '1rem',
                    backgroundColor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha('#000', 0.2)
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha('#000', 0.4)
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#000',
                      borderWidth: 1
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '1rem',
                    fontWeight: 400,
                    color: alpha('#000', 0.6),
                    '&.Mui-focused': {
                      color: '#000'
                    }
                  }
                }}
                InputProps={{
                  sx: { py: 0 }
                }}
              />
              )}

              {isPasswordReset ? (
                <TextField
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  fullWidth
                  variant="outlined"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#000', 0.2)
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#000', 0.4)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#000',
                        borderWidth: 1
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '1rem',
                      fontWeight: 400,
                      color: alpha('#000', 0.6),
                      '&.Mui-focused': {
                        color: '#000'
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          sx={{ 
                            color: 'text.secondary',
                            '&:hover': { color: '#000' }
                          }}
                        >
                          {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { py: 0 }
                  }}
                />
              ) : (email || isSignUp) && (
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  sx={{
                    mb: isSignUp ? 3 : 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#000', 0.2)
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#000', 0.4)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#000',
                        borderWidth: 1
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '1rem',
                      fontWeight: 400,
                      color: alpha('#000', 0.6),
                      '&.Mui-focused': {
                        color: '#000'
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handlePasswordVisibility}
                          edge="end"
                          sx={{ 
                            color: 'text.secondary',
                            '&:hover': { color: '#000' }
                          }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { py: 0 }
                  }}
                />
              )}

              {isPasswordReset && (
                <TextField
                  label="Confirm New Password"
                  type={showConfirmNewPassword ? 'text' : 'password'}
                  fullWidth
                  variant="outlined"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#000', 0.2)
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#000', 0.4)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#000',
                        borderWidth: 1
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '1rem',
                      fontWeight: 400,
                      color: alpha('#000', 0.6),
                      '&.Mui-focused': {
                        color: '#000'
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                          edge="end"
                          sx={{ 
                            color: 'text.secondary',
                            '&:hover': { color: '#000' }
                          }}
                        >
                          {showConfirmNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { py: 0 }
                  }}
                />
              )}

              {isSignUp && (
                <TextField
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  fullWidth
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                      fontSize: '1rem',
                      backgroundColor: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#000', 0.2)
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#000', 0.4)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#000',
                        borderWidth: 1
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '1rem',
                      fontWeight: 400,
                      color: alpha('#000', 0.6),
                      '&.Mui-focused': {
                        color: '#000'
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ 
                            color: 'text.secondary',
                            '&:hover': { color: '#000' }
                          }}
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { py: 0 }
                  }}
                />
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !isFormValid}
                sx={{
                  py: 1.5,
                  borderRadius: 4,
                  fontWeight: 500,
                  fontSize: '1rem',
                  textTransform: 'none',
                  mt: 1,
                  mb: 3,
                  backgroundColor: '#000',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: loading || !isFormValid ? alpha('#000', 0.2) : '#333'
                  },
                  '&:disabled': {
                    backgroundColor: alpha('#000', 0.2),
                    color: alpha('#fff', 0.5),
                    cursor: 'not-allowed'
                  }
                }}
              >
                {loading ? 'Please wait...' : isPasswordReset ? 'Update Password' : isSignUp ? 'Create account' : 'Continue'}
              </Button>
            </form>

            {!isSignUp && email && !isPasswordReset && (
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Link
                  variant="body2"
                  onClick={handleForgotPassword}
                  sx={{ 
                    color: '#000',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: 400,
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    opacity: loading ? 0.6 : 1,
                    '&:hover': {
                      textDecoration: loading ? 'none' : 'underline'
                    }
                  }}
                >
                  {loading ? 'Sending...' : 'Forgot your password?'}
                </Link>
              </Box>
            )}

            {!isPasswordReset && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: alpha('#000', 0.6), 
                  fontSize: '0.9rem',
                  mb: 1
                }}
              >
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}
              </Typography>
              {isSignUp ? (
                <Link
                  variant="body2"
                  onClick={() => {
                    setIsSignUp(false);
                    setError('');
                    setSuccess('');
                    setValidationMessage('');
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  sx={{ 
                    color: '#10a37f',
                    cursor: 'pointer',
                    fontWeight: 400,
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Sign in
                </Link>
              ) : (
                <Tooltip title="Sign ups are limited for now">
                  <Link
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                    sx={{ 
                      color: alpha('#000', 0.3),
                      cursor: 'not-allowed',
                      fontWeight: 400,
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      '&:hover': {
                        textDecoration: 'none'
                      }
                    }}
                  >
                    Sign up
                  </Link>
                </Tooltip>
              )}
            </Box>
            )}

            {!isPasswordReset && ENABLE_GOOGLE_AUTH && (
            <>
            <Divider sx={{ my: 3 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: alpha('#000', 0.4), 
                  fontWeight: 400,
                  px: 2,
                  fontSize: '0.85rem'
                }}
              >
                OR
              </Typography>
            </Divider>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<GoogleIcon sx={{ fontSize: 18 }} />}
              onClick={handleGoogleLogin}
              sx={{
                py: 1,
                borderRadius: 4,
                borderColor: alpha('#000', 0.2),
                color: '#000',
                fontWeight: 400,
                fontSize: '1rem',
                textTransform: 'none',
                backgroundColor: '#fff',
                '&:hover': {
                  backgroundColor: alpha('#000', 0.05),
                  borderColor: alpha('#000', 0.3)
                }
              }}
            >
              Continue with Google
            </Button>
            </>
            )}

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: alpha('#000', 0.5),
                  fontSize: '0.85rem'
                }}
              >
                &copy; {new Date().getFullYear()} GlowWorks. All rights reserved.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AuthGate;